import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { Translate } from 'react-localize-redux';
import Icon, { IconSizes, IconVariants } from '../../components/foundations/Icon';
import { getUserData } from '../../selectors/user';
import { getActiveWarnings } from '../utils';
import { HEADERS } from '../consts';
import css from '../style.css';

const isStaging = () => window.location.href.indexOf('staging.singular.net') > -1;

function WarningChip({ translationKey, className = '' }) {
    return (
        <div className={classNames(css.warningChip, className)}>
            <Icon name="alert-triangle" size={IconSizes.SMALL} variant={IconVariants.WARNING} />
            <span>
                <Translate id={translationKey} options={{ renderInnerHtml: true }} />
            </span>
        </div>
    );
}

WarningChip.propTypes = {
    translationKey: PropTypes.string.isRequired,
    className: PropTypes.string,
};

function EnvironmentBanner({ area }) {
    const userData = useSelector(getUserData) || {};
    const [shouldWarn, setShouldWarn] = useState(false);

    useEffect(() => {
        if (!(area === 'left' && isStaging())) return;
        // This endpoint returns plain "True"/"False", not the usual API envelope.
        fetch('/warning-info', { credentials: 'include' })
            .then(response => response.text())
            .then(text => setShouldWarn(text.replace(/"/g, '') === 'True'))
            .catch(() => setShouldWarn(false));
    }, [area]);

    if (area === 'left') {
        const isDeveloper = userData.account_type === 6;
        return (
            <>
                {isStaging() && (
                    <div
                        className={classNames(css.warningChip, {
                            [css.hideBelowLg]: !!userData.is_admin,
                            [css.hideBelowXlg]: !!userData.is_admin && !!userData.trial,
                        })}
                    >
                        <span>
                            <Translate id={`${HEADERS}.STAGING_WARNING`} options={{ renderInnerHtml: true }} />
                            <br />
                            <Translate id={`${HEADERS}.STAGING_INVITE_WARNING`} options={{ renderInnerHtml: true }} />
                        </span>
                        {shouldWarn && (
                            <span className={css.prodDbWarning}>
                                <Translate id={`${HEADERS}.PROD_DB_WARNING`} options={{ renderInnerHtml: true }} />
                            </span>
                        )}
                    </div>
                )}
                {userData.display_message && (
                    <div className={classNames(css.warningChip, css.hideBelowMd, { [css.developerChip]: isDeveloper })}>
                        <span>{userData.display_message}</span>
                    </div>
                )}
            </>
        );
    }

    const warnings = getActiveWarnings(userData);
    return (
        <div className={css.centerSlot}>
            {warnings.sensitiveData && (
                <WarningChip
                    translationKey={`${HEADERS}.WARNING_SENSITIVE_DATA`}
                    className={classNames(css.hideBelowSm, {
                        [css.hideBelowXlg]: !!userData.is_admin,
                        [css.hideBelowXxlg]: !!userData.is_admin && !!userData.trial,
                    })}
                />
            )}
            {warnings.blockedUserData && (
                <WarningChip translationKey={`${HEADERS}.WARNING_BLOCKED_USER_DATA`} className={css.blockedChip} />
            )}
            {warnings.highUsage && (
                <WarningChip translationKey={`${HEADERS}.WARNING_HIGH_USAGE`} className={css.highUsageChip} />
            )}
        </div>
    );
}

EnvironmentBanner.propTypes = {
    area: PropTypes.oneOf(['left', 'center']).isRequired,
};

export default EnvironmentBanner;
